import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ReportWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { ICenterBillingRecord } from '@nkr/models/index';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: ICenterBillingRecord[],
    centerId: number,
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const getTotals = (filteredList: ICenterBillingRecord[]): ICenterBillingRecord[] => {
        let result = {
            amount: 0,
            swapNumber: '',
            donorAlias: '',
            donorUnosId: '',
            donorId: 0,
            billingType: '',
            billableCenterAlias: '',
            billableCenterId: 0,
            recipientAlias: '',
            recipientId: 0,
            invoiceDate: '',
            daysOutstanding: 0,
            payableCenterId: 0,
            payableCenterAlias: '',
            organType: -1,
            payableType: '',
            opoAlias: '',
            opoId: 0,
            isPaid: false
        };

        //get transportCost Total
        result.amount = props.data.reduce((a, v) => a = a + v.amount, 0);
        result.daysOutstanding = Math.round(_.sumBy(props.data, x => x.daysOutstanding) / props.data.length);

        return [result];
    };

    const renderValue = (value: number) => {
        if (value == 0) {
            return <>-</>;
        }

        return <>{value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'Rank',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => 'Totals:',
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '')
                    return (
                        <RankRenderer {...params} />
                    );

                return null;
            },
            width: 50,
            suppressSizeToFit: true,
            sortable: false,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'billingType',
            field: 'billingType',
            headerName: 'billingType',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data.swapNumber != '') {
                    return <>{params.data.billingType}</>
                } else {
                    return 'Totals'
                }
            },
            width: 100,
            suppressSizeToFit: true
        },
        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'Swap #',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
            width: 150,
            suppressSizeToFit: true
        },

        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor</SortableHeaderComponent>,
            width: 150,
            suppressSizeToFit: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let row = params.data as ICenterBillingRecord;

                if (props.centerId == row.payableCenterId) {
                    return <a href={`/center/${row.payableCenterId}/donor/${row.donorId}/profile`} target='_blank'>{row.donorAlias}</a>;
                } else {
                    return <>{row.donorAlias}</>;
                }
            }
        },
        {
            colId: 'donorUnosId',
            field: 'donorUnosId',
            headerName: 'UNOS Donor ID',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>UNOS Donor ID</SortableHeaderComponent>,
            width: 130,
            suppressSizeToFit: true
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient</SortableHeaderComponent>,
            width: 150,
            suppressSizeToFit: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let row = params.data as ICenterBillingRecord;

                if (props.centerId == row.billableCenterId) {
                    return <a href={`center/${props.centerId}/recipient/${row.recipientId}/profile/general`}>{row.recipientAlias}</a>;
                } else {
                    return <>{row.recipientAlias}</>;
                }
            }
        },

        {
            colId: 'amount',
            field: 'amount',
            headerName: 'Amount',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Amount</SortableHeaderComponent>,
            width: 100,
            sortingOrder: ['desc', 'asc'],
            cellRendererFramework: (params: ICellRendererParams) => {
                let amount = '$' + params.data.amount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return <>{amount}</>
            }
        },

        {
            colId: 'billableCenterAlias',
            field: 'billableCenterAlias',
            headerName: 'Billable Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Billable Center</SortableHeaderComponent>,
            width: 130,
            sortingOrder: ['desc', 'asc']
        },

        {
            colId: 'isPaid',
            field: 'isPaid',
            headerName: 'Paid',
            pinnedRowCellRendererFramework: (params: ICellRendererParams) => '',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Paid</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.data.isPaid ? <span className='label label-success'>Y</span> : '-',
            width: 100,
            sortingOrder: ['desc', 'asc']
        },

        {
            colId: 'invoiceDate',
            field: 'invoiceDate',
            headerName: 'Invoice Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => moment(params.data.invoiceDate).isValid() ? moment(params.data.invoiceDate).format('MM/DD/YYYY') : '--',
            width: 110,
            sortingOrder: ['desc', 'asc']
        },
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);
    const totals = useMemo(() => getTotals(props.data), [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell-center',
                    sortable: true
                }}
                rowData={rowData}
                headerHeight={30}
                groupHeaderHeight={30}
                rowHeight={30}
                pinnedTopRowData={totals}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                excelStyles={[
                    {
                        id: 'header',
                        alignment: {
                            horizontal: 'Center'
                        },
                        font: {
                            bold: true
                        }
                    },
                    {
                        id: 'cell',
                        alignment: {
                            horizontal: 'Center'
                        }
                    },
                    {
                        id: 'totalsBackground',
                        interior: {
                            color: '#ADD8E6',
                            pattern: 'Solid',
                            patternColor: 'black'
                        }
                    }
                ]}
                noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={(e: SortChangedEvent) => { e.api.refreshCells(); }}
                onRowDataChanged={onRowDataChanged}
                getRowStyle={(props: any) => {
                    if (props.node.rowPinned) {
                        return { backgroundColor: '#d7edf8', fontWeight: 700 } as any;
                    }
                }}
            >

            </AgGridReact>
        </ReportWrapper>
    );
}

export default Grid;